import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Vapi from '@vapi-ai/web';

const UIContext = createContext();

const publicKey = process.env.REACT_APP_VAPI_PUBLIC_KEY;
const assistantId = process.env.REACT_APP_VAPI_ASSISTANT_ID;
const vapi = new Vapi(publicKey);

export const UIProvider = ({ children }) => {
  const [isRebeccaOpen, setIsRebeccaOpen] = useState(false);
  const [status, setStatus] = useState('inicio');
  const [transcription, setTranscription] = useState('');
  const [callStatus, setCallStatus] = useState('IDLE');
  const [volume, setVolume] = useState(0);
  
  const navigate = useNavigate();

  const toggleRebecca = () => setIsRebeccaOpen(prev => !prev);
  const openRebecca = () => setIsRebeccaOpen(true);
  const closeRebecca = () => setIsRebeccaOpen(false);

  useEffect(() => {
    const handleCallStart = () => {
      setCallStatus('ACTIVE');
      setStatus('escuchando');
      setTranscription('Conexión establecida. Te escucho.');
    };

    const handleCallEnd = () => {
      setCallStatus('IDLE');
      setStatus('inicio');
      setTranscription('Enlace finalizado.');
    };

    const handleSpeechStart = () => setStatus('hablando');
    const handleSpeechEnd = () => setStatus('escuchando');
    const handleVolumeLevel = (v) => setVolume(v);

    const handleMessage = (message) => {
      if (message.type === 'transcript') {
        setTranscription(message.transcript);
      }
      // Mantener tool-calls para compatibilidad con versiones del SDK
      if (message.type === 'tool-calls') {
        const toolCall = message.toolCalls?.[0];
        if (toolCall && toolCall.function?.name === 'navegar_a_seccion') {
          const args = typeof toolCall.function.arguments === 'string' 
            ? JSON.parse(toolCall.function.arguments) 
            : toolCall.function.arguments;
          ejecutarNavegacion(args.seccion, toolCall.id);
        }
      }
    };

    const handleFunctionCall = (functionCall) => {
      if (functionCall.name === 'navegar_a_seccion') {
        const { seccion } = functionCall.parameters;
        ejecutarNavegacion(seccion, functionCall.id);
      }
    };

    const handleError = (e) => {
      console.error('Vapi Error:', e);
      setTranscription('Error en la conexión neural.');
      setCallStatus('IDLE');
    };

    const ejecutarNavegacion = (seccion, callId) => {
        const normalizedSeccion = (seccion || '').toLowerCase();
        let success = false;

        const scrollAliases = {
            'inicio': 'inicio',
            'vision': 'nueva-vision',
            'servicios': 'agente-custom',
            'agente_custom': 'agente-custom',
            'marketing': 'marketing-digital',
            'diseno_web': 'diseno-web',
            'portafolio': 'servicio-portafolio',
            'prensa': 'medios',
            'directorio': 'directorio',
            'faq': 'faq',
            'portal': 'portal',
            // Alias para "saber más" o detalles
            'detalles_agente': 'agents-page',
            'detalles_marketing': 'marketing-page',
            'detalles_diseno': 'web-design-page',
            'detalles_portafolio': 'portfolio-page',
            'iniciar_proyecto': 'init-project-page'
        };

        const idReal = scrollAliases[normalizedSeccion] || normalizedSeccion;

        // Rutas internas de la aplicación
        const internalRoutes = {
            'portal': '/portal',
            'agents-page': '/agents',
            'marketing-page': '/marketing',
            'portfolio-page': '/portfolio',
            'portfolio': '/portfolio',
            'agents': '/agents',
            'marketing': '/marketing',
            'web-design-page': '/web-design',
            'init-project-page': '/init-project'
        };

        // 1. Primero verificamos si es una sección en la página principal (Home)
        const elemento = document.getElementById(idReal);

        if (elemento) {
            // Si estamos en la Home y el elemento existe, hacemos scroll
            if (window.lenis) {
                window.lenis.scrollTo(`#${idReal}`);
            } else {
                elemento.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            success = true;
            setIsRebeccaOpen(false); 
        } else {
            // 2. Si no es un elemento local, verificamos si es una ruta interna
            const route = internalRoutes[idReal];
            if (route) {
                navigate(route);
                success = true;
                setIsRebeccaOpen(false);
            }
        }

        vapi.send({
          type: 'tool-call-result',
          toolCallId: callId,
          result: success ? `Successfully moved to ${seccion}` : `Section/Action ${seccion} not found`,
        });
    };

    vapi.on('call-start', handleCallStart);
    vapi.on('call-end', handleCallEnd);
    vapi.on('speech-start', handleSpeechStart);
    vapi.on('speech-end', handleSpeechEnd);
    vapi.on('volume-level', handleVolumeLevel);
    vapi.on('message', handleMessage);
    vapi.on('function-call', handleFunctionCall);
    vapi.on('error', handleError);

    return () => vapi.removeAllListeners();
  }, [navigate]);

  const startCall = useCallback(() => {
    if (!assistantId) return;
    setCallStatus('LOADING');
    vapi.start(assistantId);
  }, []);

  const stopCall = useCallback(() => vapi.stop(), []);

  const sendTextMessage = useCallback((text) => {
    if (callStatus !== 'ACTIVE') return;
    vapi.send({
      type: 'add-message',
      message: {
        role: 'user',
        content: text,
      },
    });
  }, [callStatus]);

  const toggleCall = useCallback(() => {
    callStatus === 'ACTIVE' ? stopCall() : startCall();
  }, [callStatus, startCall, stopCall]);

  return (
    <UIContext.Provider value={{ 
      isRebeccaOpen, toggleRebecca, openRebecca, closeRebecca,
      status, setStatus, transcription, setTranscription,
      callStatus, volume, startCall, stopCall, toggleCall,
      sendTextMessage
    }}>
      {children}
    </UIContext.Provider>
  );
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) throw new Error('useUI must be used within a UIProvider');
  return context;
};
